import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Repeat } from "lucide-react";
import {
  useRecordTermSheetCounter,
  getGetContractQueryKey,
  getListContractsQueryKey,
  getListTermSheetVersionsQueryKey,
  type TermSheetVersion,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { handleApiError } from "@/lib/handleApiError";

interface CurrentTerms {
  contractType?: string | null;
  salaryRangeMin?: number | null;
  salaryRangeMax?: number | null;
  signingBonus?: number | null;
  keyTerms?: string | null;
}

interface RecordCounterOfferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  termSheetId: number;
  current: CurrentTerms;
  /** Optional: previous counter snapshot to seed the form when "countering again". */
  seedFromVersion?: TermSheetVersion | null;
  onRecorded?: () => void;
}

export function RecordCounterOfferDialog({
  open,
  onOpenChange,
  termSheetId,
  current,
  seedFromVersion,
  onRecorded,
}: RecordCounterOfferDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const recordCounter = useRecordTermSheetCounter();

  const [salaryRangeMin, setSalaryRangeMin] = useState<string>("");
  const [salaryRangeMax, setSalaryRangeMax] = useState<string>("");
  const [signingBonus, setSigningBonus] = useState<string>("");
  const [keyTerms, setKeyTerms] = useState<string>("");
  const [note, setNote] = useState<string>("");

  // Reset on open: seed with `seedFromVersion` snapshot if provided, otherwise
  // with the current term sheet's headline values so the user only edits deltas.
  useEffect(() => {
    if (!open) return;
    const src = seedFromVersion?.snapshot ?? current;
    setSalaryRangeMin(src.salaryRangeMin != null ? String(src.salaryRangeMin) : "");
    setSalaryRangeMax(src.salaryRangeMax != null ? String(src.salaryRangeMax) : "");
    setSigningBonus(src.signingBonus != null ? String(src.signingBonus) : "");
    setKeyTerms(src.keyTerms ?? "");
    setNote("");
  }, [open, seedFromVersion, current]);

  function parseNum(s: string): number | null {
    if (!s.trim()) return null;
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }

  const handleSubmit = () => {
    recordCounter.mutate(
      {
        id: termSheetId,
        data: {
          salaryRangeMin: parseNum(salaryRangeMin),
          salaryRangeMax: parseNum(salaryRangeMax),
          signingBonus: parseNum(signingBonus),
          keyTerms: keyTerms.trim() || null,
          note: note.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetContractQueryKey(termSheetId) });
          queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
          queryClient.invalidateQueries({ queryKey: getListTermSheetVersionsQueryKey(termSheetId) });
          toast({
            title: "Counter recorded",
            description: "The candidate's counter-offer has been saved.",
          });
          onOpenChange(false);
          onRecorded?.();
        },
        onError: (err) => handleApiError(toast, err, { fallbackTitle: "Could not record counter" }),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="dialog-record-counter">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Repeat className="h-5 w-5 text-amber-600" />
            Record Counter-Offer
          </DialogTitle>
          <DialogDescription>
            Capture the candidate's counter terms. This snapshots the proposed
            terms as a new version and marks the term sheet as Countered.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="counter-salary-min">Salary range min ($)</Label>
              <Input
                id="counter-salary-min"
                inputMode="numeric"
                value={salaryRangeMin}
                onChange={(e) => setSalaryRangeMin(e.target.value)}
                data-testid="input-counter-salary-min"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="counter-salary-max">Salary range max ($)</Label>
              <Input
                id="counter-salary-max"
                inputMode="numeric"
                value={salaryRangeMax}
                onChange={(e) => setSalaryRangeMax(e.target.value)}
                data-testid="input-counter-salary-max"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="counter-signing-bonus">Signing bonus ($)</Label>
            <Input
              id="counter-signing-bonus"
              inputMode="numeric"
              value={signingBonus}
              onChange={(e) => setSigningBonus(e.target.value)}
              data-testid="input-counter-signing-bonus"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="counter-key-terms">Counter key terms / notes</Label>
            <Textarea
              id="counter-key-terms"
              rows={4}
              value={keyTerms}
              onChange={(e) => setKeyTerms(e.target.value)}
              placeholder="e.g. 5-year term, mutual buyout cap of $1M, NIL parity clause…"
              data-testid="textarea-counter-key-terms"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="counter-internal-note">Internal note (optional)</Label>
            <Input
              id="counter-internal-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Context for teammates reviewing this counter"
              data-testid="input-counter-note"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={recordCounter.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={recordCounter.isPending} data-testid="button-record-counter-submit">
            {recordCounter.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Counter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
